import React, { FC, useEffect } from 'react'
import t from 'src/translations/main'
import utils from 'src/components/Organisms/Form/utils'
import useFetch from 'src/components/Core/Hooks/useFetch/useFetch'
import storage from 'src/components/Core/Utils/storage'
import updateModal from 'src/components/Sections/Modal/updateModal'
import {
  useCountryOfResidences,
  useCountryOfResidencesUpdate,
  useDropdownOptions,
} from 'src/components/Core/Context/OnboardingContext'

import Button from 'src/components/Atoms/Button/Button'
import Table from 'src/components/Organisms/Table/Table'
import Font from 'src/components/Core/Typography/Font/Font'
import ModalCountryDetailsForm from './ModalCountryDetails/ModalCountryDetailsForm'

import {
  FormStyle,
  FormHeader,
  FormTitle,
  FormDescription,
  FormFooter,
  FormFooterActionsLeft,
  FormFooterActionsRight,
} from 'src/components/Organisms/Form/Form.styles'

import {
  ApiData,
  FormProps,
  TableConfigParam,
  TableObj,
} from '../../../../Pages/Onboarding/types'
import Spinner from 'src/components/Core/Loading/Spinner/Spinner'
import { getEndpointUrl } from 'src/components/Core/Utils/url_constants'
import { Trans } from 'react-i18next'

const tableConfig: TableConfigParam[] = [
  { key: 'countryOfResidence' },
  { key: 'tin' },
  { key: 'reason' },
  { key: 'noTINReason' },
  {
    name: 'pencil',
    color: 'ultraViolet',
  },
  {
    name: 'close',
    color: 'error',
  },
  { key: 'isTINAvailable' }
]

const tableColumns = [
  {
    Header: t(
      'onboarding.forms.IndividualAccount.TaxResidencyForm.table.col1'
    ),
    accessor: 'col1',
  },
  {
    Header: t(
      'onboarding.forms.IndividualAccount.TaxResidencyForm.table.col2'
    ),
    accessor: 'col2',
  },
  {
    Header: t(
      'onboarding.forms.IndividualAccount.TaxResidencyForm.table.col3'
    ),
    accessor: 'col3',
  },
  {
    Header: t(
      'onboarding.forms.IndividualAccount.TaxResidencyForm.table.col4'
    ),
    accessor: 'col4',
  },
  {
    Header: '',
    accessor: 'col5',
  },
  {
    Header: '',
    accessor: 'col6',
  },
]

const reasonMap: Record<number, string> = {
  0: 'A',
  1: 'B'
}

const initialRenderFlag = true

const IndividualTaxResidencyForm: FC<FormProps> = props => {
  const tableData = useCountryOfResidences()
  const updateTableData = useCountryOfResidencesUpdate()
  const { dropdownOptions } = useDropdownOptions()

  const applicationId = storage.get(storage?.keys?.onboarding?.applicationId)

  const submitForm = () => {
    const { submit } = props
    if (submit) submit({data: {}})
  }

  const { sendRequest, isLoading } = useFetch()

  const editCountryDetails = (id: number) => {
    updateModal({
      title: t('onboarding.modals.CountryOfResidency.title'),
      modalState: 'open',
      content: (
        <ModalCountryDetailsForm
          actionType='edit'
          action={action}
          tableConfig={tableConfig}
          postUrl={getEndpointUrl('IndividualAOF/updatecountryofresidencydetails03')}
          id={id}
          applicationId={applicationId}
        />
      ),
      removeFooter: true,
    })
  }

  const deleteCountry = (id: number) => {
    const modifyResponse = (data: ApiData) => {
      if (typeof data === 'string' && data) {
        updateTableData((prevState: TableObj[]) => {
          return utils.deleteTableRow([...prevState], id)
        })
        updateModal({ modalState: 'close' })
      }
    }

    sendRequest(
      {
        scope: 'onboarding',
        url: getEndpointUrl('IndividualAOF/deletecountryofresidencydetails03', applicationId, 'deleteCountryOfResidenceIndividual.json', `CountryOfResidenceId=${id}`),
        method: 'DELETE',
      },
      modifyResponse
    )
  }

  const openConfirmDeleteModal = (id: number) => {
    updateModal({
      title: t('onboarding.modals.CountryOfResidency.delete.title'),
      modalState: 'open',
      content: (
        <Font>
          {t('onboarding.modals.CountryOfResidency.delete.content')}
        </Font>
      ),
      footerActions: [
        {
          label: t('components.forms.labels.yes'),
          version: 2,
          onClick: () => {
            deleteCountry(id)
          },
        },
        {
          label: t('components.forms.labels.no'),
          onClick: () => {
            updateModal({ modalState: 'close' })
          },
        },
      ],
    })
  }

  const action = (id: number, action: string) => {
    if (action === 'edit') editCountryDetails(id)
    if (action === 'delete') openConfirmDeleteModal(id)
  }

  const createTableData = (
    data: ApiData,
    tableConfig: TableConfigParam[]
  ) => {
    const newTableData: TableObj[] = []
    data?.forEach((rowDataObj: ApiData) => {
      const reasonType = reasonMap[`${rowDataObj?.reason as number}`] || 'C'
      const rowDataObjMapped = {
        ...rowDataObj,
        countryOfResidence: dropdownOptions?.countries[`${parseInt(rowDataObj?.countryOfResidence) - 1}`]?.label,
        reason: rowDataObj?.reason !== ''
          ? `Reason ${reasonType}`
          : '',
        noTINReason: (rowDataObj.noTINReason === '-' || rowDataObj.noTINReason === '')
          ? '-'
          : rowDataObj.noTINReason
      }
      const tableObj = utils.createTableObj(
        rowDataObjMapped,
        tableConfig,
        action
      )
      newTableData.push(tableObj)
    })

    return newTableData
  }

  useEffect(() => {
    const setFetchedDataHandler = (data: ApiData) => {
      if (data?.length !== 0) {
        updateTableData(
          createTableData(data, tableConfig)
        )
      }

    }

    if (initialRenderFlag !== true) return

    sendRequest({
      scope: 'onboarding',
      url: getEndpointUrl('IndividualAOF/gettaxresidency03', applicationId, 'allCountryOfResidencesIndividual.json')
    }, setFetchedDataHandler)
  }, [])

  if(initialRenderFlag && isLoading) return <Spinner removeFullscreen />

  return (
    <FormStyle>
      <FormHeader>
        <FormTitle>
          {t('onboarding.forms.IndividualAccount.TaxResidencyForm.title')}
        </FormTitle>
        <FormDescription>
          {t(
            'onboarding.forms.IndividualAccount.TaxResidencyForm.description'
          )}
        </FormDescription>
      </FormHeader>
      <Button
        version={2}
        buttonMargin='32px 0 8px 0'
        label={t('onboarding.modals.CountryOfResidency.openButton')}
        onClick={() =>
          updateModal({
            title: t('onboarding.modals.CountryOfResidency.title'),
            modalState: 'open',
            content: (
              <ModalCountryDetailsForm
                actionType='add'
                action={action}
                tableConfig={tableConfig}
                postUrl={getEndpointUrl('IndividualAOF/addcountryofresidencydetails03')}
                applicationId={applicationId}
              />
            ),
            removeFooter: true,
          })
        }
      />
      {tableData?.length !== 0 && (
        <Table tableData={tableData} tableColumns={tableColumns} />
      )}
      <Font margin='16px 0 32px 0'>
        {/* @ts-ignore:next-line */}
        <Trans i18nKey={'onboarding.forms.IndividualAccount.TaxResidencyForm.visualOnly.taxResidencyIndividualInfoBelowTheTable'} />
      </Font>
      <FormFooter noOfButtons={2}>
        <FormFooterActionsLeft>
          <Button
            label={t('onboarding.main.backButton')}
            version={2}
            disabled={props.disablePrev}
            onClick={props.prevStep}
          />
        </FormFooterActionsLeft>
        <FormFooterActionsRight>
          <Button
            label={props.isLastForm ? t('onboarding.main.saveAndSubmit') : t('onboarding.main.continueButton')}
            onClick={submitForm}
            disabled={tableData?.length === 0}
          />
        </FormFooterActionsRight>
      </FormFooter>
    </FormStyle>
  )
}

export default IndividualTaxResidencyForm
